<template>
    <v-container grid-list-xl>
        <v-col cols=11>
            <v-layout my-6>
                <v-btn @click="$router.go(-1)" class="my-auto">
                    <v-icon>navigate_before</v-icon>
                    {{ $t('back') }}
                </v-btn>

                <v-flex ml-12 xs8>
                    <div class="display-3 font-weight-thin">{{ $i18n.t('userConfig') }}</div>
                </v-flex>

                <v-btn @click="save" v-if="modified" class="my-auto mx-4" color="teal">
                    {{ $t('save') }}
                    <v-icon right>done</v-icon>
                </v-btn>
            </v-layout>

            <v-form ref="form" @submit="save">
                <v-row align="center" justify="center">
                    <v-col cols=2 align="right">
                        {{ $i18n.t('name') }}
                    </v-col>
                    <v-col cols=4 v-if="!!getUser">
                        <v-text-field dense v-model="name" :rules="nameRules" :placeholder="getUser.name" class="my-0 body-1 font-weight-light">
                            <v-tooltip slot="append" v-if="name != getUser.name" bottom>
                                <template v-slot:activator="{ on }">
                                    <v-icon v-on="on" @click="name = getUser.name">replay</v-icon>
                                </template>
                                <span>{{ $t('undo') }}</span>
                            </v-tooltip>
                        </v-text-field>
                    </v-col>
                </v-row>

                <v-row align="center" justify="center">
                    <v-col cols=2 align="right">
                        {{ $i18n.t('surname1') }}
                    </v-col>
                    <v-col cols=4 v-if="!!getUser">
                        <v-text-field dense v-model="surname1" :rules="surname1Rules" :placeholder="getUser.surname1" class="my-0 body-1 font-weight-light">
                            <v-tooltip slot="append" v-if="surname1 != getUser.surname1" bottom>
                                <template v-slot:activator="{ on }">
                                    <v-icon v-on="on" @click="surname1 = getUser.surname1">replay</v-icon>
                                </template>
                                <span>{{ $t('undo') }}</span>
                            </v-tooltip>
                        </v-text-field>
                    </v-col>
                </v-row>
                <v-row align="center" justify="center">
                    <v-col cols=2 align="right">
                        {{ $i18n.t('surname2') }}
                    </v-col>
                    <v-col cols=4 v-if="!!getUser">
                        <v-text-field dense v-model="surname2" :rules="surname2Rules" :placeholder="getUser.surname2" class="my-0 body-1 font-weight-light">
                            <v-tooltip slot="append" v-if="surname2 != getUser.surname2" bottom>
                                <template v-slot:activator="{ on }">
                                    <v-icon v-on="on" @click="surname2 = getUser.surname2">replay</v-icon>
                                </template>
                                <span>{{ $t('undo') }}</span>
                            </v-tooltip>
                        </v-text-field>
                    </v-col>
                </v-row>
                <v-row align="center" justify="center">
                    <v-col cols=2 align="right">
                        {{ $i18n.t('email') }}
                    </v-col>
                    <v-col cols=4 v-if="!!getUser">
                        <v-text-field dense v-model="email" :rules="emailRules" :placeholder="getUser.email" class="my-0 body-1 font-weight-light">
                            <v-tooltip slot="append" v-if="email != getUser.email" bottom>
                                <template v-slot:activator="{ on }">
                                    <v-icon v-on="on" @click="email = getUser.email">replay</v-icon>
                                </template>
                                <span>{{ $t('undo') }}</span>
                            </v-tooltip>
                        </v-text-field>
                    </v-col>
                </v-row>

                <v-row class="my-6">
                    <v-col cols=3></v-col>
                    <v-col cols=7>
                        <v-divider/>
                    </v-col>
                </v-row>

                <v-row align="center" justify="center">
                    <v-col cols=2 align="right">
                        {{ $i18n.t('currentPassword') }}
                    </v-col>
                    <v-col cols=4>
                        <v-text-field
                            dense
                            v-model="currentPassword"
                            :rules="currentPasswordRules"
                            :type="currentPasswordHidden ? 'password' : 'text'"
                            :append-outer-icon="currentPasswordHidden ? 'visibility' : 'visibility_off'"
                            @click:append-outer="() => (currentPasswordHidden = !currentPasswordHidden)"
                            class="my-0 body-1 font-weight-light">
                            <v-tooltip slot="append" v-if="!!currentPassword" bottom>
                                <template v-slot:activator="{ on }">
                                    <v-icon v-on="on" @click="currentPassword = ''">replay</v-icon>
                                </template>
                                <span>{{ $t('undo') }}</span>
                            </v-tooltip>
                        </v-text-field>
                    </v-col>
                </v-row>
                <v-row align="center" justify="center">
                    <v-col cols=2 align="right">
                        {{ $i18n.t('newPassword') }}
                    </v-col>
                    <v-col cols=4>
                        <v-text-field
                            dense
                            v-model="newPassword"
                            :rules="passwordRules"
                            :type="newPasswordHidden ? 'password' : 'text'"
                            :append-outer-icon="newPasswordHidden ? 'visibility' : 'visibility_off'"
                            @click:append-outer="() => (newPasswordHidden = !newPasswordHidden)"
                            class="my-0 body-1 font-weight-light">
                            <v-tooltip slot="append" v-if="!!newPassword" bottom>
                                <template v-slot:activator="{ on }">
                                    <v-icon v-on="on" @click="newPassword = ''">replay</v-icon>
                                </template>
                                <span>{{ $t('undo') }}</span>
                            </v-tooltip>
                        </v-text-field>
                    </v-col>
                </v-row>
                <v-row align="center" justify="center">
                    <v-col cols=2 align="right">
                        {{ $i18n.t('repeatPassword') }}
                    </v-col>
                    <v-col cols=4>
                        <v-text-field
                            dense
                            v-model="repeatedPassword"
                            :rules="repeatedPasswordRules"
                            :type="repeatedPasswordHidden ? 'password' : 'text'"
                            :append-outer-icon="repeatedPasswordHidden ? 'visibility' : 'visibility_off'"
                            @click:append-outer="() => (repeatedPasswordHidden = !repeatedPasswordHidden)"
                            class="my-0 body-1 font-weight-light">
                            <v-tooltip slot="append" v-if="!!repeatedPassword" bottom>
                                <template v-slot:activator="{ on }">
                                    <v-icon v-on="on" @click="repeatedPassword = ''">replay</v-icon>
                                </template>
                                <span>{{ $t('undo') }}</span>
                            </v-tooltip>
                        </v-text-field>
                    </v-col>
                </v-row>
            </v-form>
        </v-col>
    </v-container>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    data() {
        return {
            currentPasswordHidden: true,
            newPasswordHidden: true,
            repeatedPasswordHidden: true,

            nameRules: [
                v => !!v || this.$i18n.t('noName'),
                v => !v || v.length < 50 || this.$i18n.t('longName'),
            ],
            surname1Rules: [
                v => !!v || this.$i18n.t('noSurname'),
                v => !v || v.length < 50 || this.$i18n.t('longSurname'),
            ],
            surname2Rules: [
                v => !v || v.length < 50 || this.$i18n.t('longSurname'),
            ],
            emailRules: [
                v => !!v || this.$i18n.t('noEmail'),
                v => !v || /.+@.+/.test(v) || this.$i18n.t('emailNotValid'),
            ],
            currentPasswordRules: [
                v => !!v || !this.newPassword || this.$i18n.t('noPassword'),
            ],
            passwordRules: [
                v => !v || v.indexOf(' ') < 0 || this.$i18n.t('spaces'),
                v => !v || v.length >= 8 || this.$i18n.t('shortPassword'),
            ],
            repeatedPasswordRules: [
                v => v === this.newPassword || this.$i18n.t('diffPassword'),
            ],
        }
    },

    created(){
        this.$store.dispatch('getUser', this.getUserId)
        .then(() => {
            this.$store.commit('resetUserValues')
        })
    },

    methods: {
        save() {
            if (this.$refs.form.validate())
                this.$store.dispatch('updateUser')
        }
    },

    computed: {
        ...mapGetters([
            'isLoading',
            'getUserId',
            'getUser',
            'getUserName',
            'getUserSurname1',
            'getUserSurname2',
            'getUserEmail',
            'getUserCurrentPassword',
            'getUserNewPassword',
            'getUserRepeatedPassword',
        ]),
        modified() {
            if (!this.getUser) return false

            return (this.name != this.getUser.name) ||
                   (this.surname1 !== this.getUser.surname1) ||
                   (this.surname2 !== this.getUser.surname2) ||
                   (this.email !== this.getUser.email) ||
                   (this.newPassword !== "")
        },
        name: {
            get() { return this.getUserName },
            set(value) { this.$store.commit('setUserName', value) },
        },
        surname1: {
            get() { return this.getUserSurname1 },
            set(value) { this.$store.commit('setUserSurname1', value) },
        },
        surname2: {
            get() { return this.getUserSurname2 },
            set(value) { this.$store.commit('setUserSurname2', value) },
        },
        email: {
            get() { return this.getUserEmail },
            set(value) { this.$store.commit('setUserEmail', value) },
        },
        currentPassword: {
            get() { return this.getUserCurrentPassword },
            set(value) { this.$store.commit('setUserCurrentPassword', value) },
        },
        newPassword: {
            get() { return this.getUserNewPassword },
            set(value) { this.$store.commit('setUserNewPassword', value) },
        },
        repeatedPassword: {
            get() { return this.getUserRepeatedPassword },
            set(value) { this.$store.commit('setUserRepeatedPassword', value) },
        },
    },
}
</script>
